import React from "react";
import { MessageBubble } from "./MessageBubble";
import { ToolCallDisplay } from "./ToolCallDisplay";
import { Badge } from "../../../ui";

interface Message {
  role?: string;
  content?: string | Record<string, any> | null;
  tool_calls?: Array<{
    id?: string;
    type?: string;
    function?: {
      name?: string;
      arguments?: string;
    };
    // Not every producer nests the call name under `function` — some flatten it
    // onto the call itself, which is why rendering falls back to it below. The
    // type has to admit that shape for the fallback to compile.
    name?: string;
  }>;
  tool_call_id?: string;
  name?: string;
}

interface MessagesViewProps {
  messages: Message[];
  tools?: Array<{ name?: string; [key: string]: any }>;
}

/**
 * Presentational component for displaying messages from reward endpoints.
 * Shows messages with role-based coloring and handles tool calls visually.
 * Used within Breakdown section, so no card wrapper.
 */
export function MessagesView({ messages, tools }: MessagesViewProps) {
  if (messages.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {messages.map((message, index) => {
        const role = (message.role || "unknown").toLowerCase();

        // Handle assistant messages with tool calls
        if (role === "assistant" && message.tool_calls && message.tool_calls.length > 0) {
          return (
            <div key={index} className="space-y-3">
              {/* Assistant message with tool calls */}
              <MessageBubble
                role="assistant"
                content={message.content || ""}
              />
              
              {/* Tool calls */}
              <div className="ml-4 space-y-2">
                <h5 className="text-xs font-semibold text-muted-foreground mb-2">
                  Tool Calls ({message.tool_calls.length})
                </h5>
                {message.tool_calls.map((toolCall, toolCallIndex) => {
                  // Parse tool call arguments
                  let toolCallContent: any = {};
                  try {
                    if (toolCall.function?.arguments) {
                      toolCallContent = JSON.parse(toolCall.function.arguments);
                    }
                  } catch {
                    // If parsing fails, use raw string
                    toolCallContent = { raw: toolCall.function?.arguments };
                  }

                  const fullToolCall = {
                    id: toolCall.id,
                    name: toolCall.function?.name || toolCall.name,
                    parameters: toolCallContent,
                  };

                  return (
                    <MessageBubble
                      key={toolCallIndex}
                      role="tool_call"
                      content=""
                      type="tool_call"
                    >
                      <ToolCallDisplay toolCall={fullToolCall} />
                    </MessageBubble>
                  );
                })}
              </div>
            </div>
          );
        }

        // Handle tool messages (responses from tools)
        if (role === "tool") {
          return (
            <MessageBubble
              key={index}
              role="tool"
              content={message.content || ""}
              type="tool_response"
            />
          );
        }

        // Handle regular user and assistant messages
        return (
          <MessageBubble
            key={index}
            role={role}
            content={message.content || ""}
          />
        );
      })}

      {/* Show available tools if provided */}
      {tools && tools.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <h5 className="text-xs font-semibold text-muted-foreground mb-2">
            Tools Available ({tools.length})
          </h5>
          <div className="flex flex-wrap gap-2">
            {tools.map((tool, toolIndex) => (
              <Badge
                key={toolIndex}
                variant="outline"
                className="text-xs px-2 py-0.5"
              >
                {tool.name || `Tool ${toolIndex + 1}`}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
